import React, { useEffect, useState, useMemo } from 'react';
import { EditorContent } from '@tiptap/react';
import {
  X, Loader2, BookOpen, Search,
  Bold, Italic, Underline, List, ListOrdered, Quote, Code, Minus,
  Undo, Redo, Hash, Clock, AlertCircle, CheckCircle2,
  Maximize2, Minimize2, SpellCheck
} from 'lucide-react';

import GrammarCheckPanel from './GrammarCheckPanel';

const DiaryEditorModal = ({
  isOpen,
  onClose,
  isDarkMode,
  // From useDiaryEditor hook
  editor,
  executeCommand,
  liveStats,
  saveStatus,
  composeTitle, setComposeTitle,
  composeAuthor, setComposeAuthor,
  composeType, setComposeType,
  composeDate, setComposeDate,
  composeTopic, setComposeTopic,
  composePageNumber, setComposePageNumber,
  composeLink, setComposeLink,
  composeCategory, setComposeCategory,
  handleSaveDiary,
  localDraftAvailable,
  restoreLocalDraft,
  clearLocalDraft,
  grammarIssues,
  isCheckingGrammar,
  handleGrammarCheck,
  replaceGrammarFix,
  clearGrammarIssues,
  ignoreGrammarIssue,
  categories = [],
  diary = []
}) => {
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isFullWindow, setIsFullWindow] = useState(false);
  const [showBookPicker, setShowBookPicker] = useState(false);
  const [bookSearch, setBookSearch] = useState('');

  const pillStyle = { borderRadius: '9999px' };

  // Derive unique books from diary for the book picker
  const uniqueBooks = useMemo(() => {
    const map = new Map();
    (diary || []).forEach(entry => {
      const key = (entry.title || '') + '|||' + (entry.author || '');
      if (!map.has(key)) {
        map.set(key, { title: entry.title, author: entry.author, type: entry.type, count: 0 });
      }
      map.get(key).count++;
    });
    return Array.from(map.values()).sort((a, b) => (b.count || 0) - (a.count || 0));
  }, [diary]);

  const filteredBooks = useMemo(() => {
    if (!bookSearch.trim()) return uniqueBooks;
    const q = bookSearch.toLowerCase();
    return uniqueBooks.filter(b =>
      (b.title || '').toLowerCase().includes(q) || (b.author || '').toLowerCase().includes(q)
    );
  }, [uniqueBooks, bookSearch]);

  const selectBook = (book) => {
    setComposeTitle(book.title);
    setComposeAuthor(book.author);
    setComposeType(book.type || 'book');
    setShowBookPicker(false);
    setBookSearch('');
  };

  // Dynamic topics: existing topics for the currently selected book
  const existingTopics = useMemo(() => {
    if (!composeTitle.trim()) return [];
    const bookEntries = (diary || []).filter(entry => {
      const entryKey = (entry.title || '').toLowerCase().trim() + '|||' + (entry.author || '').toLowerCase().trim();
      const currentKey = composeTitle.toLowerCase().trim() + '|||' + composeAuthor.toLowerCase().trim();
      return entryKey === currentKey && entry.topic && entry.topic.trim();
    });
    const topicSet = new Set(bookEntries.map(e => e.topic.trim()));
    return Array.from(topicSet).sort();
  }, [diary, composeTitle, composeAuthor]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.keyCode === 27 && !showCancelModal && !showPublishModal) {
        handleClose();
      }
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showCancelModal, showPublishModal]);

  const handleClose = () => {
    const hasContent = composeTitle.trim() || (editor && editor.getText().trim());
    if (hasContent) {
      setShowCancelModal(true);
    } else {
      clearLocalDraft();
      onClose();
    }
  };

  const handleSaveAsDraft = async () => {
    setError('');
    await handleSaveDiary('draft');
    onClose();
  };

  const handlePublish = () => {
    if (!composeTitle.trim()) {
      setError('Please enter a reflection title.');
      return;
    }
    if (!editor || !editor.getText().trim()) {
      setError('Please write your reflection content.');
      return;
    }
    setShowPublishModal(true);
  };

  const confirmPublish = async () => {
    setShowPublishModal(false);
    await handleSaveDiary('published');
    onClose();
  };

  const discardAndClose = () => {
    clearLocalDraft();
    setShowCancelModal(false);
    onClose();
  };

  const saveDraftAndClose = async () => {
    await handleSaveDiary('draft');
    setShowCancelModal(false);
    onClose();
  };

  const handleToolbarAction = (e, cmd) => {
    e.preventDefault();
    executeCommand(cmd);
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${isFullWindow ? 'p-0' : 'p-4 md:p-6'}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-md" onClick={isFullWindow ? undefined : handleClose} />

      {/* Modal Box */}
      <div
        className={`relative shadow-2xl animate-in fade-in zoom-in duration-300 flex flex-col ${
          isFullWindow
            ? 'inset-0 w-full h-full rounded-none border-0'
            : 'w-full max-w-3xl rounded-md max-h-[90vh]'
        } ${
          isDarkMode ? 'bg-stone-900 border border-stone-800 text-white' : 'bg-white border border-stone-100 text-stone-900'
        }`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-6 md:p-8 pb-4 border-b border-stone-100 dark:border-stone-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded ${isDarkMode ? 'bg-stone-800 text-stone-300' : 'bg-stone-50 text-stone-900'}`}>
                <BookOpen size={20} />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-serif">Add Diary Entry</h3>
                <div className="flex items-center gap-4 mt-1">
                  <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-40">
                    <Hash size={10} /> {liveStats?.words || 0} Words
                  </span>
                  <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-40">
                    <Clock size={10} /> {liveStats?.time || 0} Min Read
                  </span>
                  {saveStatus === 'saving' && (
                    <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-500">
                      <Loader2 size={10} className="animate-spin" /> Saving
                    </span>
                  )}
                  {saveStatus === 'success' && (
                    <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-500">
                      <CheckCircle2 size={10} /> Saved
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setIsFullWindow(v => !v)} className={`opacity-40 hover:opacity-100 transition-opacity ${isFullWindow ? 'opacity-80' : ''}`} title={isFullWindow ? 'Exit full screen' : 'Full screen'}>
                {isFullWindow ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
              <button onClick={handleClose} className="opacity-40 hover:opacity-100 transition-opacity">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Local Draft Recovery Banner */}
          {localDraftAvailable && (
            <div className={`flex items-center justify-between p-3 rounded border mb-4 ${
              isDarkMode ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-amber-50 border-amber-100 text-amber-700'
            }`}>
              <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2">
                <AlertCircle size={12} /> Local draft found
              </span>
              <div className="flex gap-2">
                <button onClick={restoreLocalDraft} className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border ${isDarkMode ? 'border-amber-500/30 hover:bg-amber-500/20' : 'border-amber-200 hover:bg-amber-100'}`}>Restore</button>
                <button onClick={clearLocalDraft} className="text-[10px] font-bold uppercase tracking-widest px-3 py-1 opacity-50 hover:opacity-100">Dismiss</button>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 text-[10px] font-semibold uppercase tracking-widest rounded bg-red-500/10 text-red-500 border border-red-500/20 mb-2">
              {error}
            </div>
          )}
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
          {/* Metadata Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-1">Book / Article Title</label>
              <div className="flex gap-2">
                <input type="text" placeholder="e.g., The Art of Thinking" value={composeTitle} onChange={(e) => setComposeTitle(e.target.value)}
                  className={`flex-1 px-5 py-3 border outline-none transition-all text-base rounded-sm ${isDarkMode ? 'bg-stone-950 border-stone-800 focus:border-stone-600' : 'bg-stone-50 border-stone-100 focus:border-stone-300'}`} />
                <button onClick={() => setShowBookPicker(true)} style={pillStyle}
                  className={`flex-shrink-0 px-4 py-3 border text-[10px] font-bold uppercase tracking-widest transition-all ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-100 text-stone-500 hover:bg-stone-50'}`}>
                  Pick Book
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-1">Author</label>
              <input type="text" placeholder="e.g., Al-Ghazali" value={composeAuthor} onChange={(e) => setComposeAuthor(e.target.value)}
                className={`w-full px-5 py-3 border outline-none transition-all text-base rounded-sm ${isDarkMode ? 'bg-stone-950 border-stone-800 focus:border-stone-600' : 'bg-stone-50 border-stone-100 focus:border-stone-300'}`} />
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-1">Category</label>
              <select value={composeCategory} onChange={(e) => setComposeCategory(e.target.value)}
                className={`w-full px-5 py-3 border outline-none transition-all text-base rounded-sm ${isDarkMode ? 'bg-stone-950 border-stone-800 focus:border-stone-600 text-white' : 'bg-stone-50 border-stone-100 focus:border-stone-300 text-stone-900'}`}>
                {categories.length > 0 ? categories.map((cat) => (<option key={cat} value={cat}>{cat}</option>)) : (
                  <><option value="General">General</option><option value="Agama">Agama</option><option value="Falsafah">Falsafah</option><option value="Sains">Sains</option></>
                )}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-1">Type</label>
              <select value={composeType} onChange={(e) => setComposeType(e.target.value)}
                className={`w-full px-5 py-3 border outline-none transition-all text-base rounded-sm ${isDarkMode ? 'bg-stone-950 border-stone-800 focus:border-stone-600 text-white' : 'bg-stone-50 border-stone-100 focus:border-stone-300 text-stone-900'}`}>
                <option value="book">Book</option>
                <option value="article">Article</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-1">Reading Date</label>
              <input type="date" value={composeDate} onChange={(e) => setComposeDate(e.target.value)}
                className={`w-full px-5 py-3 border outline-none transition-all text-base rounded-sm ${isDarkMode ? 'bg-stone-950 border-stone-800 focus:border-stone-600' : 'bg-stone-50 border-stone-100 focus:border-stone-300'}`} />
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-1">Topic</label>
              <input type="text" placeholder="e.g., Rise of Ash'arites" value={composeTopic} onChange={(e) => setComposeTopic(e.target.value)}
                className={`w-full px-5 py-3 border outline-none transition-all text-base rounded-sm ${isDarkMode ? 'bg-stone-950 border-stone-800 focus:border-stone-600' : 'bg-stone-50 border-stone-100 focus:border-stone-300'}`} />
              {/* Dynamic topic pills: show existing topics for this book, plus default suggestions */}
              <div className="flex gap-1.5 mt-2 px-1 overflow-x-auto no-scrollbar pb-1">
                {(existingTopics.length > 0 ? existingTopics : ['Introduction', 'Preface', 'Chapter 1', 'Chapter 2', 'Chapter 3', 'Chapter 4', 'Chapter 5', 'Conclusion']).map(suggestion => {
                  const isSelected = composeTopic === suggestion;
                  const isExisting = existingTopics.includes(suggestion);
                  return (
                    <button key={suggestion} onClick={() => setComposeTopic(isSelected ? '' : suggestion)} style={pillStyle}
                      className={`px-3 py-1 text-[9px] font-bold uppercase tracking-widest border transition-all flex items-center gap-1.5 ${
                        isSelected
                          ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                          : isExisting
                            ? (isDarkMode ? 'border-stone-800 text-stone-500 opacity-60' : 'border-stone-200 text-stone-400 opacity-60')
                            : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                      }`}>
                      {isExisting && !isSelected && <span className="opacity-40">✓</span>}
                      {suggestion}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-1">Page Number</label>
              <input type="text" placeholder="e.g., Page 42-45" value={composePageNumber} onChange={(e) => setComposePageNumber(e.target.value)}
                className={`w-full px-5 py-3 border outline-none transition-all text-base rounded-sm ${isDarkMode ? 'bg-stone-950 border-stone-800 focus:border-stone-600' : 'bg-stone-50 border-stone-100 focus:border-stone-300'}`} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-1">Link</label>
              <input type="url" placeholder="https://..." value={composeLink} onChange={(e) => setComposeLink(e.target.value)}
                className={`w-full px-5 py-3 border outline-none transition-all text-base rounded-sm ${isDarkMode ? 'bg-stone-950 border-stone-800 focus:border-stone-600' : 'bg-stone-50 border-stone-100 focus:border-stone-300'}`} />
            </div>
          </div>

          {/* Formatting Toolbar */}
          <div style={{ ...pillStyle, zIndex: 10 }} className={`sticky top-0 flex overflow-x-auto no-scrollbar items-center gap-1 p-1.5 border shadow-lg backdrop-blur-xl self-start max-w-full ${isDarkMode ? 'bg-stone-900/80 border-stone-800' : 'bg-white/80 border-stone-100'}`}>
            <button onMouseDown={(e) => handleToolbarAction(e, 'bold')} className={`p-2 rounded-full ${editor?.isActive('bold') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Bold size={14} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'italic')} className={`p-2 rounded-full ${editor?.isActive('italic') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Italic size={14} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'underline')} className={`p-2 rounded-full ${editor?.isActive('underline') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Underline size={14} /></button>
            <div className="w-px h-4 bg-stone-500 mx-1 opacity-20" />
            <button onMouseDown={(e) => handleToolbarAction(e, 'h1')} className={`px-2.5 py-1.5 rounded-full text-[10px] font-bold ${editor?.isActive('heading', { level: 1 }) ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}>H1</button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'h2')} className={`px-2.5 py-1.5 rounded-full text-[10px] font-bold ${editor?.isActive('heading', { level: 2 }) ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}>H2</button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'h3')} className={`px-2.5 py-1.5 rounded-full text-[10px] font-bold ${editor?.isActive('heading', { level: 3 }) ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}>H3</button>
            <div className="w-px h-4 bg-stone-500 mx-1 opacity-20" />
            <button onMouseDown={(e) => handleToolbarAction(e, 'bullet')} className={`p-2 rounded-full ${editor?.isActive('bulletList') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><List size={14} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'number')} className={`p-2 rounded-full ${editor?.isActive('orderedList') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><ListOrdered size={14} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'blockquote')} className={`p-2 rounded-full ${editor?.isActive('blockquote') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Quote size={14} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'codeBlock')} className={`p-2 rounded-full ${editor?.isActive('codeBlock') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Code size={14} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'divider')} className="p-2 text-stone-500 hover:bg-stone-500/10 rounded-full"><Minus size={14} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'emDash')} className="p-2 text-stone-500 hover:bg-stone-500/10 rounded-full text-[14px] leading-none font-bold" title="Long dash (—)">—</button>
            <div className="w-px h-4 bg-stone-500 mx-1 opacity-20" />
            <button onClick={handleGrammarCheck} disabled={isCheckingGrammar}
              className={`p-2 rounded-full transition-all ${
                grammarIssues?.length > 0
                  ? 'text-emerald-500 bg-emerald-500/10'
                  : isCheckingGrammar
                    ? 'text-amber-500 animate-pulse'
                    : 'text-stone-500 hover:bg-stone-500/10'
              }`}
              title={isCheckingGrammar ? 'Checking...' : 'Check Grammar'}>
              {isCheckingGrammar ? <Loader2 size={14} className="animate-spin" /> : <SpellCheck size={14} />}
            </button>
            <div className="w-px h-4 bg-stone-500 mx-1 opacity-20" />
            <button onMouseDown={(e) => handleToolbarAction(e, 'undo')} className="p-2 text-stone-500 hover:bg-stone-500/10 rounded-full"><Undo size={14} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'redo')} className="p-2 text-stone-500 hover:bg-stone-500/10 rounded-full"><Redo size={14} /></button>
          </div>

          {/* Tiptap Editor */}
          <style>{`
            .diary-editor-content .ProseMirror,
            .diary-editor-content .ProseMirror p,
            .diary-editor-content .ProseMirror ul,
            .diary-editor-content .ProseMirror ol {
              font-size: 1.375rem !important;
            }
          `}</style>
          <div className="diary-editor-content prose prose-stone dark:prose-invert max-w-none leading-loose font-garamond min-h-[30vh]">
            <EditorContent editor={editor} />
          </div>

          {/* Grammar Check Panel */}
          {(grammarIssues?.length > 0 || isCheckingGrammar) && (
            <div className="mt-6">
              <GrammarCheckPanel
                issues={grammarIssues}
                isChecking={isCheckingGrammar}
                onReplace={replaceGrammarFix}
                onIgnore={ignoreGrammarIssue}
                onClose={clearGrammarIssues}
                isDarkMode={isDarkMode}
                theme={{ text: isDarkMode ? 'text-stone-100' : 'text-stone-900', textMuted: isDarkMode ? 'text-stone-400' : 'text-stone-500' }}
              />
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 p-6 md:p-8 pt-4 border-t border-stone-100 dark:border-stone-800 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3">
          <button onClick={handleClose} style={pillStyle} className={`w-full sm:w-auto px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] border transition-all ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-100 text-stone-500 hover:bg-stone-50'}`}>Cancel</button>
          <button onClick={handleSaveAsDraft} style={pillStyle} className={`w-full sm:w-auto px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] border transition-all flex items-center justify-center ${isDarkMode ? 'border-stone-800 text-stone-300 hover:border-stone-600' : 'border-stone-200 text-stone-600 hover:border-stone-400'}`}>Save as Draft</button>
          <button onClick={handlePublish} disabled={saveStatus === 'saving'} style={pillStyle} className={`w-full sm:w-auto px-8 py-3 text-[10px] font-bold uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 ${isDarkMode ? 'bg-stone-100 text-stone-900 hover:bg-stone-200' : 'bg-stone-900 text-white hover:bg-stone-800'} ${saveStatus === 'saving' ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {saveStatus === 'saving' ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            {saveStatus === 'saving' ? 'Saving...' : 'Publish'}
          </button>
        </div>
      </div>

      {/* Book Picker Modal */}
      {showBookPicker && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => { setShowBookPicker(false); setBookSearch(''); }} />
          <div className={`relative w-full max-w-md p-6 shadow-2xl border rounded-md max-h-[70vh] flex flex-col ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-serif font-bold ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>Pick a Book</h3>
              <button onClick={() => { setShowBookPicker(false); setBookSearch(''); }} className={`p-2 opacity-40 hover:opacity-100 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}><X size={18} /></button>
            </div>
            <div style={pillStyle} className={`relative flex items-center px-4 py-2.5 border mb-4 ${isDarkMode ? 'bg-stone-950 border-stone-800' : 'bg-stone-50 border-stone-100'}`}>
              <Search size={14} className="opacity-40" />
              <input type="text" placeholder="Search books..." value={bookSearch} onChange={(e) => setBookSearch(e.target.value)}
                className={`ml-3 w-full bg-transparent text-xs font-medium outline-none placeholder:opacity-40 ${isDarkMode ? 'text-white' : 'text-stone-900'}`} />
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredBooks.length === 0 ? (
                <p className={`text-center py-8 text-xs opacity-40 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>No books found</p>
              ) : (
                filteredBooks.map((book, i) => (
                  <button key={i} onClick={() => selectBook(book)}
                    style={pillStyle}
                    className={`w-full text-left px-5 py-4 text-xs font-bold uppercase tracking-[0.15em] transition-all flex items-center justify-between border ${isDarkMode
                      ? 'border-transparent hover:border-stone-800 text-stone-400 hover:text-white'
                      : 'border-transparent hover:border-stone-100 text-stone-500 hover:text-stone-900'
                    }`}>
                    <div className="min-w-0 flex-1">
                      <span className="block truncate">{book.title}</span>
                      <span className={`block text-[9px] opacity-40 mt-0.5 ${isDarkMode ? 'text-stone-500' : 'text-stone-400'}`}>{book.author || 'Unknown author'} · {book.count} {book.count === 1 ? 'reflection' : 'reflections'}</span>
                    </div>
                    <span className={`flex-shrink-0 ml-4 text-[9px] uppercase tracking-widest opacity-30 ${isDarkMode ? 'text-stone-500' : 'text-stone-400'}`}>{book.type === 'article' ? 'Article' : 'Book'}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation */}
      {showCancelModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowCancelModal(false)} />
          <div className={`relative w-full max-w-sm p-10 shadow-2xl border rounded-md ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
            <div className="text-center space-y-6">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isDarkMode ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
                <AlertCircle size={32} className="text-amber-500" />
              </div>
              <div className="space-y-2">
                <h3 className={`text-2xl font-serif font-bold ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>Unsaved Changes</h3>
                <p className={`text-sm opacity-60 ${isDarkMode ? 'text-stone-400' : 'text-stone-500'}`}>Do you want to save your work as a draft before closing?</p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <button onClick={saveDraftAndClose} style={pillStyle} className="w-full py-4 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-[0.2em] hover:opacity-90 transition-all shadow-xl">Save & Close</button>
                <button onClick={discardAndClose} style={pillStyle} className={`w-full py-4 border text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${isDarkMode ? 'border-red-500/20 text-red-400 hover:bg-red-500/10' : 'border-red-100 text-red-500 hover:bg-red-50'}`}>Discard & Close</button>
                <button onClick={() => setShowCancelModal(false)} className="w-full py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors mt-2">Continue Editing</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation */}
      {showPublishModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowPublishModal(false)} />
          <div className={`relative w-full max-w-sm p-10 shadow-2xl border rounded-md ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
            <div className="text-center space-y-6">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                <CheckCircle2 size={32} className="text-emerald-500" />
              </div>
              <div className="space-y-2">
                <h3 className={`text-2xl font-serif font-bold ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>Ready to Publish?</h3>
                <p className={`text-sm opacity-60 ${isDarkMode ? 'text-stone-400' : 'text-stone-500'}`}>This will make your reflection visible on the diary page.</p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <button onClick={confirmPublish} style={pillStyle} className="w-full py-4 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-[0.2em] hover:opacity-90 transition-all shadow-xl">Publish Now</button>
                <button onClick={() => setShowPublishModal(false)} style={pillStyle} className={`w-full py-4 border text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-100 text-stone-500 hover:bg-stone-50'}`}>Keep as Draft</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
    </div>
  );
};

export default DiaryEditorModal;